import React from "react";
import styled from "styled-components";
import {
  ProjectContainer,
  ProjectTitle,
  ProjectInfo,
} from "./styles";
import { useLanguage } from "../../contexts/LanguageContext";

const ProjectDescription = styled.div`
  margin-bottom: 30px;
  line-height: 24px;
`;

const ProjectWrapper = styled.div`
  margin-bottom: 40px;

  h4 {
    font-size: 20px;
    color: #333;
    margin: 0 0 20px 0;
    font-weight: 600;

    &.text-muted {
      color: #6c757d;
    }
  }

  p {
    line-height: 24px;
    margin-bottom: 15px;

    &.textleft {
      text-align: left;
    }

    a {
      color: #f34452;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  img {
    width: 100%;
    max-width: 600px;
    height: auto;
    margin-bottom: 20px;
    border: 1px solid #eee;

    &.img-center {
      display: block;
      margin: 0 auto 20px auto;
    }
  }

  iframe {
    width: 100%;
    max-width: 640px;
    height: 360px;
    border: 1px solid #eee;
    margin: 20px 0;
  }

  hr {
    border: 0;
    height: 1px;
    background: #eee;
    margin: 30px 0;
  }
`;

interface ProjectRapidProps {
}

const ProjectRapid: React.FC<ProjectRapidProps> = () => {
  const { t } = useLanguage();

  return (
    <ProjectContainer>
      <ProjectTitle>
        <h2>{t("rapid.title")}</h2>
        <p className="tags">{t("rapid.role")}</p>
      </ProjectTitle>

      <ProjectDescription>{t("rapid.description")}</ProjectDescription>

      <ProjectInfo>
        <p>
          <b>{t("rapid.roleLabel")}</b> {t("rapid.myRole")}
        </p>
        <p>
          <b>{t("rapid.skillsLabel")}</b> {t("rapid.skillsUtilized")}
        </p>
      </ProjectInfo>

      <ProjectWrapper>
        <img loading="lazy" decoding="async"
          className="img-center"
          src="/images/portfolio/p4-rapid-1-min.webp"
          alt="Project overview: interactive car, 3D-printed objects, and laser-cut box"
        />
      </ProjectWrapper>

      <hr />

      <ProjectWrapper>
        <h4>{t("rapid.design1")}</h4>
        <p>{t("rapid.design1Desc")}</p>
        <h4>{t("rapid.interactiveDemo")}</h4>
        <iframe
          src="https://player.vimeo.com/video/158824710"
          frameBorder="0"
          allowFullScreen
          title="Interactive Design Demo"
        ></iframe>
      </ProjectWrapper>

      <hr />

      <ProjectWrapper>
        <h4 className="text-muted">{t("rapid.design2")}</h4>
        <p className="textleft">{t("rapid.design2Desc")}</p>
        <h4>{t("rapid.2dPrinterDemo")}</h4>
        <iframe
          src="https://player.vimeo.com/video/162384768"
          frameBorder="0"
          allowFullScreen
          title="2D printer Design Demo"
        ></iframe>
      </ProjectWrapper>

      <hr />

      <ProjectWrapper>
        <h4 className="text-muted">{t("rapid.design3")}</h4>
        <p className="textleft">
          {t("rapid.design3Desc1")}{" "}
          <a
            href="https://www.youtube.com/watch?v=ztJwq9WcU6w"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("rapid.youtubeVideo")}
          </a>
        </p>
        <p className="textleft">{t("rapid.design3Desc2")}</p>
        <img loading="lazy" decoding="async"
          className="img-center"
          src="/images/projects/3d-min.webp"
          alt="2D sketches and 3D puzzle parts modeled in AutoDesk Inventor"
        />
        <h4>{t("rapid.3dPrinterDemo")}</h4>
        <iframe
          src="https://player.vimeo.com/video/165007865"
          frameBorder="0"
          allowFullScreen
          title="3D printer Design Demo"
        ></iframe>
      </ProjectWrapper>

      <hr />

      <ProjectWrapper>
        <h4 className="text-muted">{t("rapid.finalProject")}</h4>
        <p className="textleft">{t("rapid.finalProjectDesc1")}</p>
        <p className="textleft">{t("rapid.finalProjectDesc2")}</p>
        <h4>{t("rapid.presentationPoster")}</h4>
        <img loading="lazy" decoding="async"
          className="img-center"
          src="/images/projects/rapid-poster-min.webp"
          alt="Kinetic art final project presentation poster"
        />
        <h4>{t("rapid.kineticDemo")}</h4>
        <iframe
          src="https://player.vimeo.com/video/166822844"
          frameBorder="0"
          allowFullScreen
          title="Kinetic Art Demo"
        ></iframe>
      </ProjectWrapper>

      <hr />

      <ProjectInfo>
        <p>
          <b>{t("rapid.dateLabel")}</b> {t("rapid.date")}
        </p>
      </ProjectInfo>
    </ProjectContainer>
  );
};

export default ProjectRapid;
